import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { api } from '../../App';
import toast from 'react-hot-toast';
import { ApplicationFormEditor, mergeFields } from './ApplicationFormEditor';

const PLATFORMS = [['indeed', 'Indeed', 'fa-brands fa-indeed'], ['linkedin', 'LinkedIn', 'fa-brands fa-linkedin'], ['google', 'Google Jobs', 'fa-brands fa-google'], ['ziprecruiter', 'ZipRecruiter', 'fa-solid fa-bolt']];
const EMPTY_JOB = {
  title: '', role: 'closer', market: 'New York metro (5 boroughs + New Jersey)', location: '', employment_type: 'Full-time',
  compensation: { base: 0, commission_per_deal: 2500, monthly_bonus: 500, deals_per_month_low: 2, deals_per_month_high: 6, redline: 0, notes: '' },
  screening_questions: [], application_fields: [], ai_review_criteria: '',
  generate_description: true, company_name: '', ai_prompt: '', description: '',
};
const ote = (c) => ({ low: Math.round((+c.base || 0) + (+c.commission_per_deal || 0) * (+c.deals_per_month_low || 0) * 12 + (+c.monthly_bonus || 0) * 12), high: Math.round((+c.base || 0) + (+c.commission_per_deal || 0) * (+c.deals_per_month_high || 0) * 12 + (+c.monthly_bonus || 0) * 12) });
const statusChip = (s) => s === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600';

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_JOB);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try { const r = await api.get('/api/hiring/jobs'); setJobs(r.data.jobs || []); }
    catch { toast.error('Failed to load jobs'); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post('/api/hiring/jobs', form);
      toast.success(`Job "${r.data.title}" created`);
      setForm(EMPTY_JOB); setShowForm(false); load(); setDetail(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create job'); }
    setSaving(false);
  };
  const remove = async (id) => {
    try { await api.delete(`/api/hiring/jobs/${id}`); setDetail(null); load(); toast.success('Job removed'); }
    catch { toast.error('Delete failed'); }
  };

  const projected = ote(form.compensation);

  return (
    <div className="space-y-5" data-testid="jobs-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-muted">{jobs.length} job(s)</p>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm" data-testid="new-job-btn"><i className="fa-solid fa-plus mr-1.5" />New Job</button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-surface rounded-2xl border border-border p-6 space-y-5" data-testid="job-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Job title * (e.g. Solar Sales Closer)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="input-field" data-testid="job-title-input" />
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="input-field" data-testid="job-role-select">
              <option value="closer">Closer</option><option value="setter">Setter / Sales Rep</option>
            </select>
            <input placeholder="Market" value={form.market} onChange={e => setForm({ ...form, market: e.target.value })} className="input-field" />
            <input placeholder="Location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="input-field" />
            <input placeholder="Company name (used in the posting)" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className="input-field sm:col-span-2" data-testid="job-company-input" />
          </div>

          {/* Compensation / Redline builder */}
          <div className="bg-surface-alt rounded-xl p-4">
            <p className="text-xs font-bold uppercase text-text-secondary mb-3"><i className="fa-solid fa-sack-dollar mr-1.5 text-emerald-600" />Compensation / Redline Builder</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[['base', 'Base salary ($/yr)'], ['commission_per_deal', 'Commission / deal ($)'], ['monthly_bonus', 'Monthly bonus ($)'], ['redline', 'Redline ($/watt floor)'], ['deals_per_month_low', 'Deals/mo (low)'], ['deals_per_month_high', 'Deals/mo (high)']].map(([k, l]) => (
                <label key={k} className="text-[10px] font-semibold uppercase text-text-muted">{l}
                  <input type="number" min="0" value={form.compensation[k]} onChange={e => setForm({ ...form, compensation: { ...form.compensation, [k]: e.target.value } })} className="input-field text-sm mt-1" data-testid={`comp-${k}-input`} />
                </label>
              ))}
            </div>
            <input placeholder="Comp notes (accelerators, draw, etc.)" value={form.compensation.notes} onChange={e => setForm({ ...form, compensation: { ...form.compensation, notes: e.target.value } })} className="input-field text-sm mt-3" />
            <div className="mt-3 text-sm font-bold text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="ote-preview">Projected OTE: ${projected.low.toLocaleString()} – ${projected.high.toLocaleString()}/yr</div>
          </div>

          {/* Application form — questions + apply-now fields */}
          <ApplicationFormEditor
            questions={form.screening_questions}
            fields={form.application_fields}
            onQuestions={(qs) => setForm(f => ({ ...f, screening_questions: qs }))}
            onFields={(fs) => setForm(f => ({ ...f, application_fields: fs }))}
          />

          <label className="block text-[10px] font-semibold uppercase text-text-muted">AI review criteria for this role (optional — the AI judges applicants against this)
            <textarea rows={3} value={form.ai_review_criteria} onChange={e => setForm({ ...form, ai_review_criteria: e.target.value })} placeholder="e.g. Must have 2+ yrs in-home closing. Reject anyone without a vehicle. Bilingual Spanish is a big plus. Penalize vague answers." className="input-field text-sm mt-1 font-normal normal-case" data-testid="job-ai-criteria-input" />
          </label>

          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input type="checkbox" checked={form.generate_description} onChange={e => setForm({ ...form, generate_description: e.target.checked })} className="accent-emerald-600 w-4 h-4" data-testid="auto-write-toggle" />
            Auto-write the job description with AI
          </label>

          {form.generate_description ? (
            <label className="block text-[10px] font-semibold uppercase text-text-muted">AI prompt (optional — tell the AI exactly what to emphasize)
              <textarea rows={3} value={form.ai_prompt} onChange={e => setForm({ ...form, ai_prompt: e.target.value })} placeholder="e.g. Emphasize faith-driven culture, W2 with benefits, no door knocking. Mention we install in NJ + Staten Island only. Keep it under 250 words." className="input-field text-sm mt-1 font-normal normal-case" data-testid="job-ai-prompt-input" />
            </label>
          ) : (
            <label className="block text-[10px] font-semibold uppercase text-text-muted">Job description (markdown — written by you)
              <textarea rows={10} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={'## About the role\nWrite your own posting here…'} className="input-field text-sm mt-1 font-normal normal-case" data-testid="job-description-input" />
            </label>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="btn-primary text-sm" data-testid="create-job-submit"> {saving ? <i className="fa-solid fa-circle-notch fa-spin mr-1.5" /> : <i className="fa-solid fa-check mr-1.5" />}Create Job</button>
            <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_JOB); }} className="btn-secondary text-sm">Cancel</button>
          </div>
        </form>
      )}

      {/* Jobs list */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {jobs.map(j => (
          <div key={j.id} className="bg-surface rounded-2xl border border-border p-5 hover:shadow-md transition-all cursor-pointer" onClick={() => setDetail(j)} data-testid="job-card">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-bold text-text-primary">{j.title}</p>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusChip(j.status)}`}>{j.status}</span>
            </div>
            <p className="text-xs text-text-muted mt-1">{j.location || j.market}</p>
            <p className="text-xs text-emerald-700 font-semibold mt-2">OTE ${(j.compensation?.ote_low || 0).toLocaleString()} – ${(j.compensation?.ote_high || 0).toLocaleString()}</p>
            <div className="flex items-center justify-between mt-3 text-[11px] text-text-muted">
              <span><i className="fa-solid fa-users mr-1" />{j.applicants_count} applicant(s)</span>
              <span>{(j.screening_questions || []).length} screening Q</span>
            </div>
          </div>
        ))}
        {jobs.length === 0 && !showForm && <div className="col-span-full py-14 text-center text-text-muted text-sm"><i className="fa-solid fa-briefcase text-3xl opacity-20 block mb-3" />No jobs yet — create your first posting</div>}
      </div>

      {detail && <JobDetail job={detail} onClose={() => setDetail(null)} onChange={load} onDelete={remove} />}
    </div>
  );
}

function JobDetail({ job, onClose, onChange, onDelete }) {
  const [j, setJ] = useState(job);
  const [busy, setBusy] = useState('');
  const [editDesc, setEditDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(job.description || '');
  const [company, setCompany] = useState(job.company_name || '');
  const [aiPrompt, setAiPrompt] = useState(job.ai_prompt || '');
  const [editPlat, setEditPlat] = useState('');
  const [platDraft, setPlatDraft] = useState('');
  const [editApp, setEditApp] = useState(false);
  const [qDraft, setQDraft] = useState(job.screening_questions || []);
  const [fDraft, setFDraft] = useState(mergeFields(job.application_fields));
  const [critDraft, setCritDraft] = useState(job.ai_review_criteria || '');
  const posts = j.platform_posts || {};

  const saveApp = async () => {
    setBusy('saveapp');
    try {
      const r = await api.put(`/api/hiring/jobs/${j.id}`, {
        screening_questions: qDraft, application_fields: fDraft, ai_review_criteria: critDraft,
      });
      setJ({ ...j, screening_questions: r.data.screening_questions, application_fields: r.data.application_fields, ai_review_criteria: r.data.ai_review_criteria });
      setEditApp(false); onChange(); toast.success('Application form saved — live on the careers page');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };

  const saveDesc = async () => {
    setBusy('savedesc');
    try {
      await api.put(`/api/hiring/jobs/${j.id}`, { description: descDraft, company_name: company, ai_prompt: aiPrompt });
      setJ({ ...j, description: descDraft, company_name: company, ai_prompt: aiPrompt });
      setEditDesc(false); onChange(); toast.success('Description saved');
    } catch { toast.error('Save failed'); }
    setBusy('');
  };
  const savePlat = async (key) => {
    setBusy(`plat-${key}`);
    try {
      const r = await api.post(`/api/hiring/jobs/${j.id}/platform/${key}`, { content: platDraft });
      setJ({ ...j, platform_posts: r.data.platform_posts });
      setEditPlat(''); toast.success('Post copy saved');
    } catch { toast.error('Save failed'); }
    setBusy('');
  };

  const publish = async () => {
    setBusy('publish');
    try { const r = await api.post(`/api/hiring/jobs/${j.id}/publish`); setJ({ ...j, status: 'open', platform_posts: r.data.platform_posts }); onChange(); toast.success('Job is live — public link ready'); }
    catch { toast.error('Publish failed'); }
    setBusy('');
  };
  const genPosts = async () => {
    setBusy('posts');
    try { const r = await api.post(`/api/hiring/jobs/${j.id}/platform-posts`); setJ({ ...j, platform_posts: r.data.platform_posts }); toast.success('Platform posts generated'); }
    catch { toast.error('Could not generate posts'); }
    setBusy('');
  };
  const genDesc = async () => {
    setBusy('desc');
    try {
      const r = await api.post(`/api/hiring/jobs/${j.id}/generate-description`, { company_name: company, ai_prompt: aiPrompt });
      setJ({ ...j, description: r.data.description, company_name: r.data.company_name });
      setDescDraft(r.data.description); setCompany(r.data.company_name || company);
      onChange(); toast.success('Description regenerated');
    }
    catch { toast.error('Could not regenerate'); }
    setBusy('');
  };
  const markPosted = async (p, url) => {
    try { const r = await api.post(`/api/hiring/jobs/${j.id}/platform/${p}`, { status: 'posted', external_url: url || '' }); setJ({ ...j, platform_posts: r.data.platform_posts }); toast.success('Marked as posted'); }
    catch { toast.error('Failed'); }
  };
  const autoPost = async (p) => {
    setBusy(`auto-${p}`);
    try {
      const r = await api.post(`/api/hiring/jobs/${j.id}/platform/${p}/autopost`);
      setJ({ ...j, platform_posts: r.data.platform_posts });
      const res = r.data.result;
      res.ok ? toast.success(res.message) : toast(res.message, { icon: 'ℹ️', duration: 5000 });
    } catch { toast.error('Auto-post failed'); }
    setBusy('');
  };
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const publicUrl = `${base}/careers/${j.slug}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="job-detail-modal">
      <div className="bg-surface rounded-2xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-heading text-lg font-bold text-text-primary">{j.title}</h3>
              <p className="text-xs text-text-muted">{j.location || j.market} · <span className="uppercase font-bold">{j.status}</span></p>
            </div>
            <button onClick={onClose} className="text-text-muted hover:text-text-primary p-1"><i className="fa-solid fa-xmark" /></button>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {j.status !== 'open'
              ? <button onClick={publish} disabled={busy} className="btn-primary text-xs" data-testid="publish-job-btn">{busy === 'publish' ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-bullhorn mr-1" />}Publish & Go Live</button>
              : <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid="view-public-btn"><i className="fa-solid fa-arrow-up-right-from-square mr-1" />View public page</a>}
            <button onClick={genPosts} disabled={busy} className="btn-secondary text-xs" data-testid="gen-platform-posts-btn">{busy === 'posts' ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-share-nodes mr-1" />}Generate platform posts</button>
            <button onClick={genDesc} disabled={busy} className="btn-secondary text-xs" data-testid="gen-desc-btn">{busy === 'desc' ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-wand-magic-sparkles mr-1" />}Rewrite description</button>
          </div>

          {j.status === 'open' && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="public-link-box">
              <i className="fa-solid fa-link text-emerald-600 text-xs" />
              <input readOnly value={publicUrl} className="flex-1 bg-transparent text-xs text-emerald-800 font-mono truncate outline-none" />
              <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }} className="text-emerald-700 text-xs font-bold" data-testid="copy-public-link-btn"><i className="fa-solid fa-copy" /></button>
            </div>
          )}

          {/* Compensation */}
          <div className="bg-surface-alt rounded-xl p-4 text-xs text-text-secondary">
            <p className="font-bold uppercase text-text-muted mb-1">Compensation</p>
            <p>Base ${(+j.compensation?.base || 0).toLocaleString()} · ${(+j.compensation?.commission_per_deal || 0).toLocaleString()}/deal · ${(+j.compensation?.monthly_bonus || 0).toLocaleString()}/mo bonus</p>
            <p className="text-emerald-700 font-bold mt-1">OTE ${(j.compensation?.ote_low || 0).toLocaleString()} – ${(j.compensation?.ote_high || 0).toLocaleString()}/yr</p>
            {j.compensation?.notes && <p className="text-text-muted mt-1">{j.compensation.notes}</p>}
          </div>

          {/* Application form control */}
          <div data-testid="job-application-form-section">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase text-text-secondary"><i className="fa-solid fa-file-signature mr-1.5 text-violet-600" />Application Form</p>
              {!editApp
                ? <button onClick={() => { setQDraft(j.screening_questions || []); setFDraft(mergeFields(j.application_fields)); setCritDraft(j.ai_review_criteria || ''); setEditApp(true); }} className="btn-secondary text-[10px] py-0.5" data-testid="edit-application-form-btn"><i className="fa-solid fa-pen mr-1" />Edit questions &amp; fields</button>
                : <button onClick={() => setEditApp(false)} className="btn-secondary text-[10px] py-0.5">Cancel</button>}
            </div>
            {editApp ? (
              <div className="space-y-3">
                <ApplicationFormEditor questions={qDraft} fields={fDraft} onQuestions={setQDraft} onFields={setFDraft} />
                <label className="block text-[10px] font-semibold uppercase text-text-muted">AI review criteria for this role
                  <textarea rows={3} value={critDraft} onChange={e => setCritDraft(e.target.value)} placeholder="What should the AI reward or reject when it reviews applicants for this job?" className="input-field text-sm mt-1 font-normal normal-case" data-testid="detail-ai-criteria-input" />
                </label>
                <button onClick={saveApp} disabled={busy} className="btn-primary text-xs" data-testid="save-application-form-btn">{busy === 'saveapp' ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-check mr-1" />}Save application form</button>
              </div>
            ) : (
              <div className="bg-surface-alt rounded-xl p-3 text-[11px] text-text-secondary space-y-1" data-testid="application-form-summary">
                <p>{(j.screening_questions || []).length} screening question(s) · {(j.screening_questions || []).filter(q => q.mandatory).length} mandatory</p>
                <p>{mergeFields(j.application_fields).filter(f => f.enabled).length} of {mergeFields(j.application_fields).length} apply-now fields shown · {mergeFields(j.application_fields).filter(f => f.enabled && f.mandatory).length} required</p>
                {(j.screening_questions || []).map((q, i) => (
                  <p key={i} className="text-text-muted">• {q.question}{q.mandatory && <span className="ml-1 text-rose-600 font-bold">required</span>}</p>
                ))}
                {j.ai_review_criteria && <p className="text-text-muted pt-1"><i className="fa-solid fa-robot mr-1" />AI criteria: {j.ai_review_criteria}</p>}
              </div>
            )}
          </div>

          {/* Multi-platform posting */}
          <div>
            <p className="text-xs font-bold uppercase text-text-secondary mb-2"><i className="fa-solid fa-tower-broadcast mr-1.5 text-primary" />Multi-Platform Posting</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {PLATFORMS.map(([key, label, icon]) => {
                const p = posts[key] || {};
                return (
                  <div key={key} className="border border-border rounded-xl p-3" data-testid={`platform-${key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-text-primary"><i className={`${icon} mr-1.5`} />{label}</span>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${p.status === 'posted' || p.status === 'posted_api' ? 'bg-emerald-100 text-emerald-700' : p.status === 'ready' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{p.status === 'posted_api' ? 'posted (API)' : (p.status || 'not generated')}</span>
                    </div>
                    {p.content ? (
                      <>
                        {editPlat === key ? (
                          <>
                            <textarea rows={7} value={platDraft} onChange={e => setPlatDraft(e.target.value)} className="input-field text-[11px] mb-2" data-testid={`edit-${key}-textarea`} />
                            <div className="flex gap-1.5">
                              <button onClick={() => savePlat(key)} disabled={busy} className="btn-primary text-[10px] py-0.5" data-testid={`save-${key}-btn`}>{busy === `plat-${key}` ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-check mr-1" />}Save copy</button>
                              <button onClick={() => setEditPlat('')} className="btn-secondary text-[10px] py-0.5">Cancel</button>
                            </div>
                          </>
                        ) : (
                        <>
                        <p className="text-[11px] text-text-muted line-clamp-3 mb-2">{p.content}</p>
                        <div className="flex flex-wrap gap-1.5">
                          <button onClick={() => { setEditPlat(key); setPlatDraft(p.content); }} className="btn-secondary text-[10px] py-0.5" data-testid={`edit-${key}-btn`}><i className="fa-solid fa-pen mr-1" />Edit</button>
                          <button onClick={() => { navigator.clipboard.writeText(p.content); toast.success(`${label} copy ready`); }} className="btn-secondary text-[10px] py-0.5" data-testid={`copy-${key}-btn`}><i className="fa-solid fa-copy mr-1" />Copy</button>
                          {j.status === 'open' && <button onClick={() => { navigator.clipboard.writeText(`${publicUrl}?src=${key}`); toast.success(`${label} tracked apply link copied — applicants from it show in Analytics`); }} className="btn-secondary text-[10px] py-0.5" data-testid={`share-${key}-btn`}><i className="fa-solid fa-link mr-1" />Tracked link</button>}
                          {key !== 'google' && <button onClick={() => autoPost(key)} disabled={busy} className="btn-secondary text-[10px] py-0.5" data-testid={`autopost-${key}-btn`}>{busy === `auto-${key}` ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-bolt mr-1" />}Auto-post</button>}
                          {p.post_url && <a href={p.post_url} target="_blank" rel="noreferrer" className="btn-secondary text-[10px] py-0.5"><i className="fa-solid fa-arrow-up-right-from-square mr-1" />Post on {label}</a>}
                          {p.status !== 'posted' && p.status !== 'posted_api' && <button onClick={() => markPosted(key)} className="btn-secondary text-[10px] py-0.5" data-testid={`mark-${key}-btn`}>Mark posted</button>}
                        </div>
                        </>
                        )}
                      </>
                    ) : (
                      editPlat === key ? (
                        <>
                          <textarea rows={7} value={platDraft} onChange={e => setPlatDraft(e.target.value)} placeholder={`Write the ${label} post yourself…`} className="input-field text-[11px] mb-2" data-testid={`edit-${key}-textarea`} />
                          <div className="flex gap-1.5">
                            <button onClick={() => savePlat(key)} disabled={busy} className="btn-primary text-[10px] py-0.5" data-testid={`save-${key}-btn`}>Save copy</button>
                            <button onClick={() => setEditPlat('')} className="btn-secondary text-[10px] py-0.5">Cancel</button>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[11px] text-text-muted opacity-60">Generate posts, or write it yourself.</p>
                          <button onClick={() => { setEditPlat(key); setPlatDraft(''); }} className="btn-secondary text-[10px] py-0.5" data-testid={`edit-${key}-btn`}><i className="fa-solid fa-pen mr-1" />Write</button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-text-muted mt-2">Google Jobs indexes your public careers page automatically. For Indeed/LinkedIn/ZipRecruiter, paste the tailored post + its tracked apply link (so Analytics attributes every applicant to the right platform), or connect a partner API key to auto-publish.</p>
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase text-text-secondary">Job Description</p>
              {!editDesc && <button onClick={() => { setDescDraft(j.description || ''); setEditDesc(true); }} className="btn-secondary text-[10px] py-0.5" data-testid="edit-desc-btn"><i className="fa-solid fa-pen mr-1" />Edit</button>}
            </div>
            {editDesc ? (
              <div className="space-y-2">
                <div className="grid sm:grid-cols-2 gap-2">
                  <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company name used in the posting" className="input-field text-xs" data-testid="detail-company-input" />
                  <input value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="AI prompt for rewrites (optional)" className="input-field text-xs" data-testid="detail-ai-prompt-input" />
                </div>
                <textarea rows={14} value={descDraft} onChange={e => setDescDraft(e.target.value)} className="input-field text-sm font-mono" data-testid="edit-desc-textarea" />
                <div className="flex gap-2">
                  <button onClick={saveDesc} disabled={busy} className="btn-primary text-xs" data-testid="save-desc-btn">{busy === 'savedesc' ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-check mr-1" />}Save description</button>
                  <button onClick={() => setEditDesc(false)} className="btn-secondary text-xs">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="md-doc text-sm bg-surface-alt rounded-xl p-4 max-h-72 overflow-y-auto" data-testid="job-description"><ReactMarkdown>{j.description || ''}</ReactMarkdown></div>
            )}
          </div>

          <div className="flex justify-between pt-1">
            <button onClick={() => onDelete(j.id)} className="text-rose-600 text-xs font-semibold hover:underline"><i className="fa-solid fa-trash-can mr-1" />Delete job</button>
            <button onClick={onClose} className="btn-secondary text-xs">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
